/*
 * <cart-rewards-fx> — polish for the cart rewards bar:
 *   1. tweens the fill width when the bar re-renders (cart changes), and
 *   2. fires a subtle confetti burst when a milestone is newly reached.
 *
 * The bar is server-rendered and replaced wholesale on every cart update, so a
 * plain CSS transition can't survive the swap. Instead we watch the drawer for
 * re-renders, remember the previous fill % / reached-states, and animate the new
 * bar from the old values. Reading state straight from the DOM (not from the
 * cart-update event) makes this robust no matter which code path re-rendered.
 *
 * Mounted once outside #CartDrawer so it persists across re-renders.
 * Confetti is optional — degrades silently if canvas-confetti isn't present.
 */
class CartRewardsFx extends HTMLElement {
  connectedCallback() {
    this.progress = null; // last fill %
    this.reached = {}; // last is-reached state, per milestone
    const drawer = document.querySelector('cart-drawer') || document.body;
    this.observer = new MutationObserver(() => this.sync(true));
    this.observer.observe(drawer, { childList: true, subtree: true });
    this.sync(false); // prime state without animating or celebrating
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  sync(active) {
    const bar = document.querySelector('.cart-rewards');
    if (!bar) {
      this.progress = null;
      this.reached = {};
      return;
    }

    const target = parseFloat(bar.style.getPropertyValue('--rewards-progress')) || 0;
    const fill = bar.querySelector('.cart-rewards__fill');
    if (active && fill && this.progress !== null && Math.abs(this.progress - target) > 0.1) {
      fill.style.transition = 'none';
      fill.style.width = `${this.progress}%`;
      void fill.offsetWidth; // reflow so the start width sticks before we tween
      fill.style.transition = 'width 0.6s cubic-bezier(0.22, 1, 0.36, 1)';
      fill.style.width = `${target}%`;
    }
    this.progress = target;

    const justReached = [];
    bar.querySelectorAll('.cart-rewards__point').forEach((point) => {
      const key = point.style.gridColumn || point.className;
      const isReached = point.classList.contains('is-reached');
      if (active && isReached && this.reached[key] === false) justReached.push(point.style.gridColumn);
      this.reached[key] = isReached;
    });
    if (justReached.length) this.celebrate(justReached);
  }

  // Confetti bound to our own full-viewport canvas. The global confetti() hard-codes
  // its canvas to z-index 100, which renders *behind* the cart drawer (z-index 1000);
  // this canvas sits above everything so the burst is actually visible.
  fireConfetti() {
    if (this._fire !== undefined) return this._fire;
    if (typeof confetti === 'function' && typeof confetti.create === 'function') {
      const canvas = document.createElement('canvas');
      canvas.className = 'cart-rewards-confetti';
      Object.assign(canvas.style, {
        position: 'fixed',
        inset: '0',
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: '2147483647',
      });
      document.body.appendChild(canvas);
      this._fire = confetti.create(canvas, { resize: true, useWorker: true });
    } else {
      this._fire = null;
    }
    return this._fire;
  }

  // Burst once the drawer has finished opening, at each newly-reached marker.
  celebrate(columns) {
    const fire = this.fireConfetti();
    if (!fire) return;
    setTimeout(() => {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || !drawer.classList.contains('active')) return;

      const surface = document.querySelector('.drawer__inner') || document.body;
      const styles = getComputedStyle(surface);
      const token = (name) => `rgb(${(styles.getPropertyValue(name) || '').trim() || '0,0,0'})`;
      const colors = [token('--color-button'), token('--color-foreground'), '#ffffff'];

      columns.forEach((col) => {
        const point = document.querySelector(`.cart-rewards__point[style*="grid-column: ${col}"]`);
        const rect = point && point.getBoundingClientRect();
        if (!rect || !rect.width) return;
        fire({
          particleCount: 90,
          spread: 70,
          startVelocity: 32,
          scalar: 0.8,
          gravity: 0.9,
          disableForReducedMotion: true,
          colors,
          origin: {
            x: (rect.left + rect.width / 2) / window.innerWidth,
            y: (rect.top + rect.height / 2) / window.innerHeight,
          },
        });
      });
    }, 400);
  }
}

customElements.define('cart-rewards-fx', CartRewardsFx);
